/**
 * DocumentsTab — file upload/download/delete via R2 + D1
 * Uses real API calls, not in-memory state.
 */

import { useState, useCallback } from 'react'
import type { ProjectDetailProject, ProjectFile } from '../../../types'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  /** Pre-loaded files from ProjectDetailResponse */
  initialFiles?: ProjectFile[]
}

const FILE_TYPES = [
  'Report',
  'Patent',
  'Agreement',
  'Presentation',
  'Other',
]

const getFileIcon = (fileType: string): string => {
  switch (fileType) {
    case 'Report':
      return '📄'
    case 'Patent':
      return '⚖️'
    case 'Agreement':
      return '📋'
    case 'Presentation':
      return '🎯'
    default:
      return '📎'
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

export function DocumentsTab({
  project,
  projectId,
  domain,
  onFieldChange,
  initialFiles = [],
}: TabContentProps) {
  const [files, setFiles] = useState<ProjectFile[]>(initialFiles)
  const [newFileType, setNewFileType] = useState('Report')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    if (selected.length === 0) return

    setUploading(true)
    setError(null)

    for (const file of selected) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('file_type', newFileType)

        const token = localStorage.getItem('hos_token') || ''
        const res = await fetch(`/api/v2/projects/${projectId}/files`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })

        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Upload failed' }))
          throw new Error(err.error || `Upload failed (${res.status})`)
        }

        const saved: ProjectFile = await res.json()
        setFiles(prev => [saved, ...prev])
      } catch (err: any) {
        setError(err.message || 'Upload failed')
      }
    }

    setUploading(false)
    // Reset file input
    e.target.value = ''
  }, [projectId, newFileType])

  const handleRemoveFile = useCallback(async (fileId: number) => {
    try {
      const token = localStorage.getItem('hos_token') || ''
      const res = await fetch(`/api/v2/projects/${projectId}/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) throw new Error('Delete failed')

      setFiles(prev => prev.filter(f => f.id !== fileId))
    } catch (err: any) {
      setError(err.message || 'Delete failed')
    }
  }, [projectId])

  const handleDownloadFile = useCallback(async (file: ProjectFile) => {
    try {
      const token = localStorage.getItem('hos_token') || ''
      const res = await fetch(`/api/v2/projects/${projectId}/files/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Download failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.original_name || file.filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message || 'Download failed')
    }
  }, [projectId])

  // Group files by type
  const filesByType = FILE_TYPES.reduce((acc, type) => {
    acc[type] = files.filter((f) => (f.file_type || 'Other') === type)
    return acc
  }, {} as Record<string, ProjectFile[]>)

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-[#1e1e38] border border-dashed border-[#2a2a44] rounded-xl p-5">
        <h3 className="text-[13px] font-medium text-[#e8e8f0] mb-4">
          Upload Document
        </h3>

        {error && (
          <div className="mb-3 px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-lg text-[12px] text-red-300">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            File Type
          </label>
          <select
            value={newFileType}
            onChange={(e) => setNewFileType(e.target.value)}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          >
            {FILE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="
          relative
          border-2 border-dashed border-[#2a2a44]
          rounded-lg p-6
          text-center
          hover:border-[#3a3a54] transition-colors
          cursor-pointer
        ">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          {uploading ? (
            <div className="text-[14px] font-medium text-[#a8a8c0] animate-pulse">
              Uploading...
            </div>
          ) : (
            <>
              <div className="text-[14px] font-medium text-[#a8a8c0]">
                Click or drag files here
              </div>
              <div className="text-[12px] text-[#8b8ba0] mt-1">
                PDF, Word, Excel, PowerPoint
              </div>
            </>
          )}
        </div>
      </div>

      {/* Files by Type */}
      {Object.entries(filesByType).map(([type, typeFiles]) => (
        typeFiles.length > 0 && (
          <div key={type}>
            <h3 className="text-[13px] font-medium text-[#e8e8f0] mb-3 flex items-center gap-2">
              <span className="text-[18px]">{getFileIcon(type)}</span>
              {type}
            </h3>

            <div className="space-y-2">
              {typeFiles.map((file) => (
                <div
                  key={file.id}
                  className="
                    bg-[#1e1e38] border border-[#2a2a44]
                    rounded-lg p-3
                    flex items-center justify-between
                  "
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[#e8e8f0] truncate">
                      {file.original_name || file.filename}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[11px] text-[#a8a8c0]">
                        {file.file_size ? formatFileSize(file.file_size) : 'Unknown size'}
                      </span>
                      <span className="text-[11px] text-[#8b8ba0]">
                        {file.uploaded_at &&
                          new Date(file.uploaded_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => handleDownloadFile(file)}
                      className="
                        px-3 py-1
                        bg-[#12122a] border border-[#2a2a44]
                        rounded text-[12px] text-[#a8a8c0]
                        hover:border-[#3a3a54] hover:text-[#e8e8f0]
                        transition-colors
                      "
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      className="
                        px-3 py-1
                        text-[12px] text-[#E61B25]
                        hover:text-[#d41620]
                        font-medium
                      "
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {files.length === 0 && (
        <div className="
          bg-[#1e1e38] border border-[#2a2a44]
          rounded-xl p-8
          text-center text-[13px] text-[#a8a8c0]
        ">
          No documents uploaded yet. Upload your first file above.
        </div>
      )}

      {/* Files Statistics */}
      {files.length > 0 && (
        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5">
          <h3 className="text-[13px] font-medium text-[#e8e8f0] mb-4">
            Document Summary
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3 text-center">
              <div className="text-[15px] font-semibold text-[#e8e8f0]">
                {files.length}
              </div>
              <div className="text-[12px] text-[#a8a8c0] mt-1">Total Files</div>
            </div>

            <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3 text-center">
              <div className="text-[15px] font-semibold text-[#e8e8f0]">
                {formatFileSize(
                  files.reduce((sum, f) => sum + (f.file_size || 0), 0)
                )}
              </div>
              <div className="text-[12px] text-[#a8a8c0] mt-1">Total Size</div>
            </div>

            <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3 text-center">
              <div className="text-[15px] font-semibold text-[#e8e8f0]">
                {FILE_TYPES.filter((type) =>
                  files.some((f) => (f.file_type || 'Other') === type)
                ).length}
              </div>
              <div className="text-[12px] text-[#a8a8c0] mt-1">File Types</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentsTab
